import React, { useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WidgetScreen } from "../../common/WidgetScreen";

import { WidgetOverviewSettings } from "../WidgetOverviewSettings";
import { useWidgetSignalOverviewPreviewQuery } from "./useWidgetSignalOverviewPreviewQuery";
import { WidgetSignalOverviewPreview } from "./WidgetSignalOverviewPreview";
import { WSSignalOverviewMutation } from "./__generated__/WSSignalOverviewMutation.graphql";
import { WSSignalOverviewQuery } from "./__generated__/WSSignalOverviewQuery.graphql";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { WidgetScreenPreview } from "../WidgetScreenPreview/WidgetScreenPreview";

export type WSSignalOverviewParams = object;

const WS_SIGNAL_OVERVIEW_QUERY = graphql`
  query WSSignalOverviewQuery($id: ID!) {
    store {
      ...WidgetOverviewSettings_Store
      publicTeams {
        id
      }
    }
    widgetSignalOverview(id: $id) {
      id
      teams {
        id
      }
    }
  }
`;

export const WSSignalOverview = withWebModal(WSSignalOverviewC, true);
function WSSignalOverviewC(
  props: UserLoggedInNavigatorScreenProps<"WSSignalOverview">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerWSSignalOverview {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWSSignalOverview(
  props: UserLoggedInNavigatorScreenProps<"WSSignalOverview"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WSSignalOverviewQuery>(
    WS_SIGNAL_OVERVIEW_QUERY,
    {
      id: props.route.params.widgetId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const [teamIds, _setTeamIds] = useState<string[]>(
    (data.widgetSignalOverview?.teams.length ?? 0) ===
      data.store.publicTeams.length
      ? []
      : (data.widgetSignalOverview?.teams.map(({ id }) => id) ?? [])
  );

  const [save, loading] = useMutation<WSSignalOverviewMutation>(graphql`
    mutation WSSignalOverviewMutation(
      $storecastId: ID!
      $widgetId: ID!
      $update: WidgetSignalOverviewUpdateInput!
    ) {
      dashboard(dashboardId: $storecastId) {
        updateWidgetSignalOverview(id: $widgetId, update: $update) {
          id
          ...WidgetSignalOverview_widget
          teams {
            id
            name
          }
        }
      }
    }
  `);

  const onSave = useCallback(() => {
    save({
      variables: {
        storecastId: props.route.params.storecastId,
        update: {
          setTeamIds: teamIds,
        },
        widgetId: props.route.params.widgetId,
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
    });
  }, [
    props.navigation,
    props.route.params.storecastId,
    props.route.params.widgetId,
    save,
    teamIds,
  ]);

  const [query, setTeamIds] = useWidgetSignalOverviewPreviewQuery({
    storecastId: props.route.params.storecastId,
    teamIds,
    setTeamIds: _setTeamIds,
  });

  return (
    <WidgetScreen
      settings={
        <WidgetOverviewSettings
          store={data.store}
          setTeamIds={setTeamIds}
          teamIds={teamIds}
        />
      }
      preview={
        <WidgetScreenPreview
          onSave={onSave}
          loading={loading}
          size={props.route.params.size}
          unitSize={props.route.params.unitSize}
        >
          {query ? (
            <WidgetSignalOverviewPreview
              query={query}
              storecastId={props.route.params.storecastId}
            />
          ) : null}
        </WidgetScreenPreview>
      }
    />
  );
}
